import {
  downloadFile as downloadFileApi,
  File as PspFile,
  Folder,
  getMandateStructure,
} from "@/api/openapi";
import { useIsTablet } from "@/config/hooks";
import { translate } from "@/config/translator";
import { TableColumn } from "@/config/types";
import {
  downloadBlob,
  downloadFile,
  getFileFromBlobResponse,
  getFileOrFolderFromPath,
} from "@/config/utils";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { decodePathFromUrl } from "../submittedDocumentsUtils";
import { PspContext } from "@/config/contexts";

export function useSubmittedDocuments() {
  // -----------------------------------------------------------------------------
  // GENERAL
  // -----------------------------------------------------------------------------
  const { lang, selectedMandateId } = useContext(PspContext);
  const isTablet = useIsTablet();
  const { "*": path } = useParams();
  const decodedPath = decodePathFromUrl(path);
  const navigate = useNavigate();

  // -----------------------------------------------------------------------------
  // STATES
  // -----------------------------------------------------------------------------
  const [structure, setStructure] = useState<Folder>();
  const [currentStructure, setCurrentStructure] = useState<Folder | PspFile>();
  const [fileActionOpenPath, setFileActionOpenPath] = useState<string | null>(
    null,
  );
  const [currentFilePreview, setCurrentFilePreview] = useState<File | null>(
    null,
  );

  // -----------------------------------------------------------------------------
  // FETCH INITIAL DATA
  // -----------------------------------------------------------------------------
  useEffect(() => {
    if (selectedMandateId) {
      getMandateStructure({ path: { mandate_id: selectedMandateId } }).then(
        ({ data }) => {
          if (!data) throw new Error("Could not get mandate structure.");
          setStructure(data);
        },
        () => {
          toast(translate("couldntLoadMandateStructure", lang));
        },
      );
    }
  }, [lang, selectedMandateId]);

  useEffect(() => {
    if (structure) {
      setCurrentStructure(
        getFileOrFolderFromPath(structure, decodedPath ?? structure.path),
      );
    }
  }, [currentStructure?.path, decodedPath, structure]);

  const columns: TableColumn[] = [
    { key: "type", label: translate("type", lang), className: "w-10" },
    { key: "name", label: translate("name", lang), className: "w-1/2" },
    {
      key: "lastEditedAt",
      label: translate("lastEditedAt", lang),
      className: "min-w-30 w-1/4",
    },
    {
      key: "size",
      label: translate("size", lang),
      className: "min-w-24 w-1/4",
    },
    { key: "action", label: translate("action", lang), className: "w-20" },
  ];

  function onDownload(filePath?: string) {
    if (typeof filePath === "string") {
      downloadFileApi({ query: { path: filePath }, responseType: "blob" }).then(
        (response) => {
          if (response.error) {
            toast(translate("somethingWentWrong", lang));
          } else {
            downloadBlob(response);
          }
        },
      );
    } else if (currentFilePreview) {
      downloadFile(currentFilePreview);
      setCurrentFilePreview(null);
    } else {
      toast(translate("somethingWentWrong", lang));
    }
  }

  function onOpenPreview(filePath: string) {
    downloadFileApi({ query: { path: filePath }, responseType: "blob" }).then(
      (response) => {
        if (response.error) {
          toast(translate("somethingWentWrong", lang));
        } else {
          setFileActionOpenPath(null);
          setCurrentFilePreview(getFileFromBlobResponse(response));
        }
      },
    );
  }

  return {
    selectedMandateId,
    isTablet,
    decodedPath,
    columns,
    currentStructure,
    navigate,
    onDownload,
    lang,
    fileActionOpenPath,
    setFileActionOpenPath,
    onOpenPreview,
    currentFilePreview,
    setCurrentFilePreview,
  };
}
